import * as ko from 'knockout';
import {_} from 'lodash';
import {TBaseOrObservableOrFunctionArrayType, TBaseOrObservableOrFunctionType} from '../../index';
import {NO_DATA_MESSAGE, PLEASE_WAIT_MESSAGE} from '../../constants/Language';
import {getAnyValue, getNextId} from '../../util/misc';
import {isNullOrUndefined} from '../../util/value';
import {nsDom} from '../../util/dom';
import v2Html from './v2.html?raw';

export let template = v2Html;

/** Row height (px) assumed until a rendered option can be measured. Mirrors `.combobox-v2__option` in v2.scss. */
const VIRTUAL_ROW_HEIGHT_FALLBACK = 30
/** Scroll viewport height (px) assumed until the list element can be measured (first open). */
const VIRTUAL_VIEWPORT_HEIGHT_FALLBACK = 320
/** Extra rows rendered above and below the viewport so fast scrolling / key-repeat never shows blanks. */
const VIRTUAL_OVERSCAN_ROWS = 30

/** Dropdown caret color variant for dark vs light backgrounds. */
export type TComboboxV2Caret = 'default' | 'light'

/**
 * Visual chrome for the search input (border + leading icon).
 * Not form/submit validation — use ErrorManager / callers for that.
 */
export type TComboboxV2InputStatus = 'ok' | 'warning' | 'info'

/**
 * Typed attributes applied to the combobox search input.
 * Each key accepts a plain value, Knockout observable/computed, or zero-arg function
 * (same unwrap rules as {@link getAnyValue} / `enable`).
 */
export interface IComboboxV2InputAttrs {
    /** Allow `lm.t(...)` (`string | null`); resolved via {@link getAnyValue}. */
    placeholder?: TBaseOrObservableOrFunctionType<string | null>
    title?: TBaseOrObservableOrFunctionType<string | null>
    maxLength?: TBaseOrObservableOrFunctionType<number>
    ariaLabel?: TBaseOrObservableOrFunctionType<string | null>
    /** Prevent 1Password from treating a non-credential search field as a login input. */
    onePasswordIgnore?: TBaseOrObservableOrFunctionType<boolean>
    /**
     * Native `autocomplete` on the search input. Default `'off'` when omitted.
     */
    autocomplete?: TBaseOrObservableOrFunctionType<string | null>
    /**
     * Overrides the auto-generated input id when set.
     * Resolved once at construct (DOM id must stay stable for `onInputReady` / anchors).
     */
    id?: TBaseOrObservableOrFunctionType<string | null>
}

export interface IComboboxV2Options<T> {
    /**
     * Sync list source. Mutually exclusive with {@link promise}
     * (DEBUG assert if both or neither).
     * Pass one of `data` or `promise`.
     */
    data?: TBaseOrObservableOrFunctionArrayType<T>
    /**
     * Async list source. Factory is invoked on construct and on {@link reload}.
     * Mutually exclusive with {@link data} (DEBUG assert if both or neither).
     */
    promise?: () => Promise<T[]>
    /**
     * Assigned by the viewModel: re-run {@link promise} (or re-read sync {@link data}).
     * Wrappers keep this on their options object (e.g. after create/update).
     */
    reload?: () => void
    /**
     * When this observable notifies, call {@link reload} (legacy select `updateOnObservableChange`).
     * Typical triggers: language / source-translation changes that affect labels or list contents.
     */
    reloadWhen?: KnockoutObservable<any> | KnockoutObservableArray<any>
    /**
     * False until the first successful load (and briefly during {@link reload}).
     * Caller may pass an observable; otherwise one is created and written back to params.
     */
    isReady?: KnockoutObservable<boolean>
    /**
     * True while a {@link promise} factory call is in flight.
     * Caller may pass an observable; otherwise one is created and written back to params.
     */
    isLoading?: KnockoutObservable<boolean>
    /** Invoked when {@link promise} rejects (after ignoring stale generations). */
    onError?: (reason: unknown) => void
    /** Invoked from {@link viewModel.dispose} (e.g. abort an in-flight adapted request). */
    onDispose?: () => void
    /**
     * List transforms applied whenever source data is (re)loaded
     * ({@link viewModel.applyPreparedItems} / sync {@link data} notify / promise settle).
     *
     * Steps always run in this fixed order (object key order is ignored):
     * filter → reduce → map → sort → reverse.
     *
     * Re-runs when an observable `data` notifies. Results are kept on an internal list;
     * the caller's `data` observable is never written back to.
     */
    prepareData?: {
        filter?: (item: T) => boolean
        reduce?: (items: any[]) => T[]
        map?: (items: T[]) => T[]
        sort?: (items: T[]) => T[]
        reverse?: boolean
    }
    /**
     * After {@link prepareData}, before `_data` is written and initial match runs.
     * Mutate `items` in place or return a replacement array (legacy select `onDataReady`).
     * Runs on every load/reload (sync `data` notify, promise settle, {@link reload}).
     */
    onItemsPrepared?: (items: T[]) => T[] | void
    /**
     * When false the input is disabled and the list cannot open.
     * If present, takes precedence over `disable` (same as combobox/base).
     * Omitted / undefined ⇒ enabled. Accepts boolean, observable, or function.
     */
    enable?: TBaseOrObservableOrFunctionType<boolean>
    /**
     * When true the input is disabled. Ignored if `enable` is also set.
     * Omitted / undefined ⇒ enabled.
     */
    disable?: TBaseOrObservableOrFunctionType<boolean>
    /**
     * Bound to the search/filter input.
     * Caller may pass an observable; otherwise one is created and written back to params.
     */
    input?: KnockoutObservable<string>
    /**
     * Currently selected item (full object or null). Created if omitted.
     * Prefer full list items — use {@link initialMatch} when you only have an id/key until data loads.
     * Do not put an id-only stub when there is no selection (`{id: 0}` etc.): v2 syncs any
     * non-null `selected` to the input via {@link inputAfterSelect} or {@link option.label};
     * a missing label falls back to `String(item)` → `[object Object]`. Use `null` until a
     * real id exists, or seed `{id}` only for a positive/known id that will match after load.
     */
    selected?: KnockoutObservable<T | null>
    /**
     * When the list first becomes ready: pick the row to select if `selected` is still empty
     * (or only matched by `.id`). Predicate over prepared list items — not a partial stub in `selected`.
     */
    initialMatch?: (item: T) => boolean
    /**
     * After initial match resolution (legacy select `onInitialObj`).
     * Receives the matched item (or `undefined`) and the search {@link input} observable —
     * not the viewModel. Return `true` to latch {@link inputStatus} chrome immediately
     * (e.g. seed unmatched text then show info).
     */
    onInitialMatchResolved?: (item: T | undefined, input: KnockoutObservable<string>) => boolean
    /** Per-option presentation and live list behaviour. */
    option?: {
        /**
         * List-row / filter / sort / width text, and the accessible name.
         * Also the default committed input value when {@link IComboboxV2Options.inputAfterSelect}
         * is omitted. Default: `String(item)`. Null/undefined coerced to `''` / `String(item)`
         * inside the viewModel.
         */
        label?: (item: T) => string | null | undefined
        /**
         * HTML for the option row (legacy base `map2list`). When set, rows use `html` binding;
         * {@link label} still drives filtering, matching, and width estimates (and the input
         * unless {@link IComboboxV2Options.inputAfterSelect} is set).
         */
        html?: (item: T) => string
        /** Native `title` tooltip on each option row. Also used as the search-input tooltip when selected and {@link IComboboxV2Options.title} is omitted (legacy `itemFn.title` fallback). Null/undefined omitted from the DOM title. */
        tooltip?: (item: T) => string | null | undefined
        /**
         * Stable identity for “same choice” (skip re-select, selected highlight).
         * When set, two items with equal keys count as the same even if references differ.
         * Omitted: reference equality only (`===`).
         */
        key?: (item: T) => string | number
        /**
         * Sort keys for the displayed list (lodash sortBy).
         * Omitted: sort by lowercase `label`. `false`: keep source order.
         */
        sortBy?: false | ((item: T) => any)[]
        /**
         * Always-on row constraint (legacy `itemFn.filter` first phase).
         * Runs on every list render; `query` is already lowercased (may be empty).
         */
        constrain?: (item: T, query: string) => boolean
        /**
         * Live text filter while the user is editing (legacy label-includes second phase).
         * Omitted: case-insensitive substring on `label`.
         * `false`: never text-filter. Function: `query` is already lowercased.
         */
        match?: false | ((item: T, query: string) => boolean)
        /**
         * When true, apply {@link match} even with an empty query / list opened without typing.
         * Default false (match only while filterActive with a non-empty query).
         * Ignored when {@link constrain} is set — constraint-only legacy filters use constrain alone on open.
         */
        matchWhenEmpty?: boolean
        /**
         * When text-filtering: put startsWith matches before other matches (legacy select).
         * Default true. Only applies while filterActive and `sortBy` is not false.
         */
        preferPrefixMatches?: boolean
        /**
         * When true, option labels are single-line with text-overflow ellipsis (fixed-height rows).
         * Default false: labels may wrap to multiple lines.
         * Forced on by {@link IComboboxV2Options.virtualize}; usable on its own for dense lists.
         */
        textEllipsis?: boolean
        /**
         * Extra horizontal space reserved ahead of the label when estimating virtualized list width
         * (icon, badge, gap). CSS length, e.g. `'1.5em'` or `'24px'`. Default none (0).
         * Combined as chrome + longest-label `ch` + row padding; see `--combobox-v2-option-chrome`.
         */
        leadingChrome?: string
        /**
         * Maps to `data-type` on the option row (legacy `itemFn.attr` → `{ 'data-type': … }`).
         * Used by call-site SCSS (e.g. system vs role styling).
         */
        dataType?: (item: T) => string | null | undefined
        /**
         * When true the row is non-selectable (legacy `itemFn.disabled`).
         * Skipped by click / Enter / keyboard highlight; still visible in the list.
         */
        disabled?: (item: T) => boolean
    }
    /** Attributes for the search `<input>`. */
    inputAttrs?: IComboboxV2InputAttrs
    /**
     * Dynamic `title` tooltip on the search input (legacy select top-level `title`).
     * Called with (selected, inputText). Precedence for the input tooltip:
     * {@link title} → {@link option.tooltip} when selected → {@link inputAttrs}.title.
     */
    title?: (selected: T | null, text: string) => string | null | undefined
    /**
     * When the prepared list is empty (after load), replace the input with the same chrome as
     * {@link isLoading}, using this message (translation key string, e.g. legacy `no data`).
     * Omitted ⇒ show an empty ready input (select facade default).
     */
    emptyPreparedListMessage?: string | null
    /**
     * Shown in the open list when the user has typed a query and no options match
     * (legacy base `emptyStateMessage`). Omitted ⇒ hide the list when the filter is empty
     * (legacy select behaviour). Distinct from {@link emptyPreparedListMessage} (no data at all).
     */
    emptyFilterMessage?: string | null
    /**
     * When true, an unmatched blur (or Enter with the list closed) restores the last
     * selected item's label instead of clearing the selection (legacy `keepLastValidObject`).
     * Default false: unmatched text clears `selected` and keeps the typed string (legacy).
     */
    restoreLastValidOnBlur?: boolean
    /**
     * Visual chrome for the input (border + icon). Latched on blur/commit (and programmatic
     * `selected` sync); cleared on focus — same timing as legacy `validate` / show-warning.
     * Omitted ⇒ legacy default (`warning` when text is present but nothing is selected).
     * Explicit legacy `validate` maps here via the select facade.
     */
    inputStatus?: (selected: T | null, text: string) => TComboboxV2InputStatus
    /**
     * When true, list DOM is created on first open. When false (default), it mounts at
     * construction so the first open is cheap. Either way the list stays mounted afterwards
     * and visibility is CSS via {@link isOpen} / `.combobox-v2--open`.
     */
    lazyMountList?: boolean
    /**
     * Render only the options around the scroll viewport instead of the whole list.
     * Opt-in — omitted / `false` keeps the full DOM list. Implies `option.textEllipsis`
     * (virtualization needs single-line, fixed-height rows), so callers never have to set both.
     * List width is `max(input, chrome + longestLabel·ch)`, capped at 50dvw — longest label is
     * counted once from the prepared list (stable while filtering/scrolling).
     */
    virtualize?: boolean
    /**
     * `select` (default): commit item to `selected` and sync input label.
     * `pick`: fire `onSelect`, clear input, leave `selected` empty, keep the list open
     * (pick-and-add flows — legacy `vm.input('')` + `isListHiddenDueToSelection(false)`).
     */
    selectionMode?: 'select' | 'pick'
    /**
     * Canonical input text when a selection is committed or synced into the field
     * (list pick, programmatic `selected`, initialMatch, blur restore). Falls back to
     * {@link option.label}. Legacy: `onSelect` + `vm.input(name_full)`.
     */
    inputAfterSelect?: (item: T) => string
    onSelect?: (item: T | null) => void
    /** Fires once when the search input element exists in the DOM. */
    onInputReady?: (input: HTMLInputElement) => void
    /** Extra CSS class on the root element (e.g. legacy theme hooks). */
    wrapperClass?: string
    /** Extra CSS class on the search `<input>` (legacy base `css.input`). */
    inputClass?: string
    /** Caret color variant. Default `default` (dark caret for light backgrounds). */
    caret?: TComboboxV2Caret
    /**
     * When true (default), the search input uses `text-overflow: ellipsis` when the value overflows
     * (mainly visible while unfocused — browsers drop it while editing). Pass `false` to opt out.
     * Distinct from {@link option.textEllipsis} (dropdown option rows).
     */
    inputTextEllipsis?: boolean
}

export class viewModel<T> {
    private readonly componentId = `combobox-v2-${getNextId()}`
    private readonly listId = `${this.componentId}-list`
    /** Scroll container around the listbox — the element the virtual window is measured against. */
    private readonly scrollElementId = `${this.componentId}-scroll`
    /**
     * CSS anchor names are dashed-idents, not custom properties, so they cannot be var()-substituted
     * and must be unique per instance — otherwise every list would tether to the last input on the page.
     * Written as inline declarations; browsers without anchor positioning just drop them.
     */
    private readonly inputAnchorStyle = `anchor-name: --${this.componentId}`
    /** Inline styles for the scroll list: position-anchor plus virtualized width CSS variables. */
    private readonly listDomStyle: KnockoutComputed<string>
    public readonly input: KnockoutObservable<string>
    public readonly selected: KnockoutObservable<T | null>
    private readonly isOpen = ko.observable(false)
    /**
     * `isOpen` plus rows to show: filtered options, or the empty-filter message
     * ({@link IComboboxV2Options.emptyFilterMessage}). Legacy select hides at length 0.
     */
    public readonly isListVisible: KnockoutComputed<boolean>
    /** Open list + typed query + zero matches + {@link IComboboxV2Options.emptyFilterMessage}. */
    public readonly showEmptyFilterMessage: KnockoutComputed<boolean>
    /** Plain text for {@link showEmptyFilterMessage} (legacy base uses `text:`, not `t:`). */
    public readonly emptyFilterMessageText: string | undefined
    /**
     * List DOM mount flag. Eager by default ({@link IComboboxV2Options.lazyMountList} false);
     * with lazyMountList, set true on first open. Stays mounted; visibility is CSS via isOpen.
     */
    private readonly listMounted = ko.observable(false)
    /**
     * Text filter stays off until the user edits the field (click/↑↓ reopen show the full list).
     * Matches legacy select isListFiltered behaviour.
     */
    private readonly filterActive = ko.observable(false)
    /** Keyboard / pointer highlight — the option Enter would commit. */
    private readonly currentItem: KnockoutObservable<T | null> = ko.observable(null)
    /**
     * Ignores pointer-driven highlight briefly after keyboard scroll/open so a stationary
     * cursor does not steal currentItem from the arrow-key target (same idea as base.silentMouse).
     */
    private ignorePointerHighlight = false
    private readonly items: KnockoutComputed<T[]>
    /** Total option count — `aria-setsize`, since a virtualized list only renders a window. */
    private readonly optionCount: KnockoutComputed<number>
    /** Half-open `[start, end)` slice of {@link items} that is rendered. Whole list when not virtualizing. */
    private readonly visibleWindow: KnockoutComputed<{start: number, end: number}>
    /** Absolute index of the first rendered option — the template adds `$index()` to it. */
    private readonly windowStartIndex: KnockoutComputed<number>
    /** Absolute index just past the last rendered option. */
    private readonly windowEndIndex: KnockoutComputed<number>
    private readonly visibleItems: KnockoutComputed<T[]>
    /** Spacer padding standing in for the un-rendered rows so the scrollbar spans the whole list. */
    private readonly optionsSpacerStyle: KnockoutComputed<{paddingTop: string, paddingBottom: string}>
    /** When true the list uses a virtual window and a stable width estimate. */
    public readonly isVirtualized: KnockoutComputed<boolean>
    /**
     * Longest prepared-label length in characters — drives `--combobox-v2-estimate-ch`.
     * From {@link _data}, not the live filter window, so typing does not resize the list.
     */
    private readonly maxLabelChars = ko.observable(0)
    private readonly scrollTop = ko.observable(0)
    private readonly viewportHeight = ko.observable(VIRTUAL_VIEWPORT_HEIGHT_FALLBACK)
    private readonly rowHeight = ko.observable(VIRTUAL_ROW_HEIGHT_FALLBACK)
    private scrollElement: HTMLElement | null = null
    private viewportObserver: ResizeObserver | null = null
    private readonly activeDescendantId: KnockoutComputed<string | undefined>
    private readonly inputDomAttrs: KnockoutComputed<{
        id: string
        style: string
        placeholder: string | undefined
        title: string | undefined
        maxlength: number | undefined
        autocomplete: string
        'aria-label': string | undefined
        'aria-controls': string
        'aria-expanded': string
        'aria-activedescendant': string | undefined
        'aria-disabled': string
        'data-1p-ignore': string | undefined
    }>
    /** enable wins if present; else invert disable; else true (combobox/base). Also false while loading. */
    private readonly isEnabled: KnockoutComputed<boolean>
    private readonly enableSubscription: KnockoutSubscription
    private readonly selectedSubscription: KnockoutSubscription
    /** Present when `params.data` is observable — keeps prepared `_data` in sync with the source. */
    private readonly dataSubscription: KnockoutSubscription | undefined
    /** Present when {@link IComboboxV2Options.reloadWhen} is set — calls {@link reload} on notify. */
    private readonly reloadWhenSubscription: KnockoutSubscription | undefined
    public readonly wrapperClass: string | undefined
    public readonly inputClass: string | undefined
    public readonly isCaretLight: boolean
    /**
     * `loading` | `empty` | `ready` — empty prepared list uses the same chrome as loading
     * when {@link IComboboxV2Options.emptyPreparedListMessage} is set.
     */
    public readonly inputPhase: KnockoutComputed<'loading' | 'empty' | 'ready'>
    /** True when option.html is configured — template picks html vs text binding once. */
    public readonly hasOptionHtml: boolean
    /** When true, option labels ellipsis on one line ({@link IComboboxV2Options.option} `.textEllipsis`). */
    public readonly isTextEllipsis: KnockoutComputed<boolean>
    /** When true, search input ellipsis when value overflows ({@link IComboboxV2Options.inputTextEllipsis}). */
    public readonly isInputTextEllipsis: boolean
    /**
     * Latched {@link IComboboxV2Options.inputStatus} for CSS chrome (legacy default when omitted).
     * Not live while typing — refreshed on blur/commit; cleared to `ok` on focus.
     */
    public readonly inputChromeStatus: KnockoutObservable<TComboboxV2InputStatus>
    /** Working list after prepareData. Never aliases `params.data`. */
    private readonly _data: KnockoutObservable<T[]>
    private readonly inputElementId: string
    /** Set on window blur — next pointer on an already-focused input should select-all again. */
    private selectAllPending = false
    /** True for this pointer gesture when select-all should win over caret placement. */
    private selectAllThisGesture = false
    /**
     * When true, the selected→input subscription skips (legacy `listenToSelected(false)`).
     * Set around {@link commitSelection} with `syncInput: false` so unmatched settle can
     * clear selection without wiping typed text.
     */
    private suppressSelectedInputSync = false
    /** Bumped on each {@link reload} / dispose so overlapping promise settles are ignored. */
    private loadGeneration = 0
    public readonly isReady: KnockoutObservable<boolean>
    public readonly isLoading: KnockoutObservable<boolean>
    /** Plain please-wait + spinner shown while {@link isLoading} (no alert chrome). */
    public readonly loadingMessage = PLEASE_WAIT_MESSAGE
    /** Shown in the empty phase (list-only, prepared data empty, message configured). */
    public readonly emptyDataMessage: string

    constructor(private readonly params: IComboboxV2Options<T>) {
        








        this.input = params.input ?? ko.observable('')
        params.input = this.input
        this.selected = params.selected ?? ko.observable<T | null>(null)
        this.wrapperClass = params.wrapperClass
        this.inputClass = params.inputClass
        this.hasOptionHtml = typeof params.option?.html === 'function'
        this.emptyDataMessage = params.emptyPreparedListMessage ?? NO_DATA_MESSAGE
        this.emptyFilterMessageText = params.emptyFilterMessage ?? undefined
        this.isCaretLight = params.caret === 'light'
        // Internal prepared list only — never write back to the caller's observable.
        this._data = ko.observable([] as T[])
        this.isVirtualized = ko.pureComputed(() => this.params.virtualize === true)
        // Virtualization needs fixed-height rows, so it turns ellipsis on for the caller.
        this.isTextEllipsis = ko.pureComputed(
            () => params.option?.textEllipsis === true || this.isVirtualized(),
        )
        this.isInputTextEllipsis = params.inputTextEllipsis !== false
        this.inputElementId = getAnyValue(params.inputAttrs?.id) ?? this.componentId

        const hasPromise = typeof params.promise === 'function'
        this.isReady = params.isReady ?? ko.observable(!hasPromise)
        this.isLoading = params.isLoading ?? ko.observable(hasPromise)
        params.isReady = this.isReady
        params.isLoading = this.isLoading
        params.reload = () => this.reload()

        // Latched chrome (legacy: validate on commit, clear on focus) — not live while typing.
        // Created before the first data load so onInitialMatchResolved can latch status immediately.
        this.inputChromeStatus = ko.observable<TComboboxV2InputStatus>('ok')

        if (hasPromise) {
            this.dataSubscription = undefined
            this.reload()
        } else {
            this.refreshFromSource()
            this.dataSubscription = ko.isObservable(params.data)
                ? (params.data as KnockoutObservable<T[]>).subscribe(() => this.refreshFromSource())
                : undefined
            this.isReady(true)
            this.isLoading(false)
        }

        this.reloadWhenSubscription = params.reloadWhen
            ? params.reloadWhen.subscribe(() => this.reload())
            : undefined

        // After initialMatch / existing selected, show the label (avoid empty until first blur).
        // Do not clear input when nothing is selected — onInitialMatchResolved may have seeded unmatched text.
        // Callers: any non-null selected is labeled here before list match — see `selected` JSDoc
        // (id-only stubs without a label field → "[object Object]").
        const initialSelected = this.selected()
        if (initialSelected != null) {
            this.input(this.inputTextForSelection(initialSelected))
        }

        // Default: mount list now so the first open does not pay foreach cost.
        if (!params.lazyMountList) {
            this.listMounted(true)
        }

        // Keep input in sync with programmatic selection changes, but never while the
        // user is actively using the open list (avoid disrupting typing/highlight UX).
        // Also skip when commitSelection(..., false) is clearing/changing selected without
        // wanting the input rewritten (legacy listenToSelected(false) on unmatched blur).
        this.selectedSubscription = this.selected.subscribe(newSelected => {
            if (this.suppressSelectedInputSync || this.isOpen()) {
                return
            }
            this.input(newSelected != null ? this.inputTextForSelection(newSelected) : '')
            this.refreshInputChromeStatus()
        })

        this.inputPhase = ko.pureComputed(() => {
            if (this.isLoading()) {
                return 'loading'
            }
            if (
                this.params.emptyPreparedListMessage
                && this.isReady()
                && (this._data()?.length ?? 0) === 0
                // Keep ready/disabled input when enable is false (e.g. role picker waiting for system).
                && (!('enable' in this.params) || (getAnyValue(this.params.enable) ?? true))
            ) {
                return 'empty'
            }
            return 'ready'
        })

        this.isEnabled = ko.pureComputed(() => {
            if (this.isLoading() || this.inputPhase() === 'empty') {
                return false
            }
            if ('enable' in this.params) {
                return getAnyValue(this.params.enable) ?? true
            }
            if ('disable' in this.params) {
                return !(getAnyValue(this.params.disable) ?? false)
            }
            return true
        })
        this.enableSubscription = this.isEnabled.subscribe(enabled => {
            if (!enabled) {
                this.closeList()
            }
        })
        window.addEventListener('blur', this.onWindowBlur)
        this.items = ko.pureComputed(() => {
            let data = this._data() ?? []
            const needle = (this.input() ?? '').toLowerCase()
            const constrain = this.params.option?.constrain
            if (constrain) {
                data = data.filter(item => constrain(item, needle))
            }

            const matchOpt = this.params.option?.match
            const matchWhenEmpty = this.params.option?.matchWhenEmpty === true
            const runTextMatch = matchOpt !== false && (
                constrain
                    ? this.filterActive() && !!needle
                    : (this.filterActive() && !!needle) || matchWhenEmpty
            )

            if (runTextMatch) {
                const match = typeof matchOpt === 'function'
                    ? matchOpt
                    : (item: T, query: string) => this.optionLabel(item).toLowerCase().includes(query)
                data = data.filter(item => match(item, needle))
            }

            const sortBy = this.params.option?.sortBy
            if (sortBy === false) {
                return data
            }

            const sortKeys: ((item: T) => any)[] = []
            if (
                this.filterActive()
                && needle
                && this.params.option?.preferPrefixMatches !== false
            ) {
                sortKeys.push((item: T) => !this.optionLabel(item).toLowerCase().startsWith(needle))
            }
            sortKeys.push(...(sortBy ?? [(item: T) => this.optionLabel(item).toLowerCase()]))
            return _.sortBy(data, sortKeys)
        })
        this.optionCount = ko.pureComputed(() => this.items().length)
        // Match legacy base showEmptyState: open + non-empty typed query + zero filtered rows.
        this.showEmptyFilterMessage = ko.pureComputed(() => {
            if (!this.emptyFilterMessageText) {
                return false
            }
            if (!this.isOpen() || this.optionCount() > 0) {
                return false
            }
            return !!(this.input() ?? '').trim()
        })
        this.isListVisible = ko.pureComputed(() =>
            this.isOpen() && (this.optionCount() > 0 || this.showEmptyFilterMessage()),
        )
        this.visibleWindow = ko.pureComputed(() => {
            const total = this.optionCount()
            if (!this.isVirtualized()) {
                return {start: 0, end: total}
            }
            const rowHeight = this.rowHeight()
            const firstVisibleRow = Math.floor(this.scrollTop() / rowHeight)
            const visibleRows = Math.ceil(this.viewportHeight() / rowHeight)
            return {
                start: Math.max(0, firstVisibleRow - VIRTUAL_OVERSCAN_ROWS),
                end: Math.min(total, firstVisibleRow + visibleRows + VIRTUAL_OVERSCAN_ROWS),
            }
        })
        // Reading the bounds as numbers keeps knockout's primitive equality check in play: scrolling
        // within one row re-evaluates visibleWindow but notifies nothing further.
        this.windowStartIndex = ko.pureComputed(() => this.visibleWindow().start)
        this.windowEndIndex = ko.pureComputed(() => this.visibleWindow().end)
        this.visibleItems = ko.pureComputed(() => {
            const items = this.items()
            const start = this.windowStartIndex()
            const end = this.windowEndIndex()
            // Same item references on every window shift, so knockout only creates/removes the edge rows.
            return start === 0 && end === items.length ? items : items.slice(start, end)
        })
        this.optionsSpacerStyle = ko.pureComputed(() => {
            const rowHeight = this.rowHeight()
            return {
                paddingTop: `${this.windowStartIndex() * rowHeight}px`,
                paddingBottom: `${(this.optionCount() - this.windowEndIndex()) * rowHeight}px`,
            }
        })
        this.activeDescendantId = ko.pureComputed(() => {
            if (!this.isOpen()) {
                return undefined
            }
            const current = this.currentItem()
            if (current == null) {
                return undefined
            }
            const index = this.items().indexOf(current)
            return index >= 0 ? this.optionId(index) : undefined
        })
        this.inputDomAttrs = ko.pureComputed(() => {
            const a = this.params.inputAttrs
            const titleFn = this.params.title
            const selected = this.selected()
            let title: string | undefined
            if (titleFn) {
                title = titleFn(selected, this.input()) ?? undefined
            } else if (selected != null && this.params.option?.tooltip) {
                // Legacy: itemFn.title doubles as input tooltip while a choice is selected.
                title = this.params.option.tooltip(selected) || undefined
            } else {
                title = getAnyValue(a?.title) ?? undefined
            }
            return {
                id: this.inputElementId,
                style: this.inputAnchorStyle,
                placeholder: getAnyValue(a?.placeholder) ?? undefined,
                title,
                maxlength: getAnyValue(a?.maxLength),
                autocomplete: getAnyValue(a?.autocomplete) ?? 'off',
                'aria-label': getAnyValue(a?.ariaLabel) ?? undefined,
                'aria-controls': this.listId,
                'aria-expanded': this.isListVisible().toString(),
                'aria-activedescendant': this.activeDescendantId(),
                'aria-disabled': (!this.isEnabled()).toString(),
                'data-1p-ignore': getAnyValue(a?.onePasswordIgnore) ? 'true' : undefined,
            }
        })
        // Virtualized width: anchor + longest-label ch (+ optional leading chrome).
        this.listDomStyle = ko.pureComputed(() => {
            const parts = [`position-anchor: --${this.componentId}`]
            if (this.isVirtualized()) {
                parts.push(`--combobox-v2-estimate-ch: ${this.maxLabelChars()}ch`)
                const chrome = this.params.option?.leadingChrome
                if (chrome) {
                    parts.push(`--combobox-v2-option-chrome: ${chrome}`)
                }
            }
            return parts.join('; ')
        })

        void this.notifyInputReady()
    }

    /** Wait for the input node, then invoke `onInputReady` once. */
    private async notifyInputReady(): Promise<void> {
        if (!this.params.onInputReady) {
            return
        }
        const el = await nsDom.waitForElementId(this.inputElementId)
        this.params.onInputReady(el as HTMLInputElement)
    }

    /**
     * Apply prepareData and {@link IComboboxV2Options.onItemsPrepared}, write `_data`,
     * then reconcile selection / open highlight. Used for sync `data` reads and promise settles.
     */
    private applyPreparedItems(rawItems: T[]): void {
        let items = rawItems
        const prepare = this.params.prepareData
        if (prepare) {
            if (prepare.filter) {
                items = items.filter(prepare.filter)
            }
            if (prepare.reduce) {
                items = prepare.reduce(items)
            }
            if (prepare.map) {
                items = prepare.map(items)
            }
            if (prepare.sort) {
                items = prepare.sort(items)
            }
            if (prepare.reverse) {
                items = items.slice().reverse()
            }
        }
        const prepared = this.params.onItemsPrepared?.(items)
        if (prepared != null) {
            items = prepared
        }
        this._data(items)

        const selected = this.selected()
        const id = (selected as {id?: unknown} | null)?.id
        let matched: T | undefined
        if (!isNullOrUndefined(id)) {
            matched = items.find(o => (o as {id?: unknown})?.id == id)
        } else if (selected != null && this.params.option?.key) {
            // Prefer stable key when selected is already a full object (e.g. org picker).
            matched = items.find(o => this.isSameSelection(o, selected))
        } else if (this.params.initialMatch) {
            matched = items.find(o => this.params.initialMatch!(o))
        } else if (selected != null) {
            matched = items.find(o => this.isSameSelection(o, selected))
        }

        // Legacy order: callback before applying selection (may seed input when unmatched).
        const onInitialMatchResolvedResponse = this.params.onInitialMatchResolved?.(matched, this.input)
        if (onInitialMatchResolvedResponse === true) {
            this.refreshInputChromeStatus()
        }

        if (matched != null) {
            const current = this.selected()
            if (!this.isSameSelection(matched, current)) {
                this.selected(matched)
            } else if (current != null && current !== matched) {
                // Same stable key, different object. Rewriting selected notifies callers that
                // rebuild `data` and can loop — keep selected when it already has a usable label.
                // Id stubs / unresolved objects still need an upgrade (and input sync).
                const matchedText = this.inputTextForSelection(matched)
                const currentText = this.inputTextForSelection(current)
                const currentLooksUnresolved =
                    !currentText || currentText === '[object Object]'
                if (currentLooksUnresolved && matchedText && matchedText !== currentText) {
                    this.selected(matched)
                } else if (!this.suppressSelectedInputSync && !this.isOpen() && this.input() !== matchedText) {
                    this.input(matchedText)
                    this.refreshInputChromeStatus()
                }
            }
        } else if (selected != null && items.length > 0) {
            // Selected item is not in a non-empty prepared list (filter/reload).
            // Do not clear when the list is still empty (e.g. sync `data` before first load) —
            // that would wipe remembered selections (login org) and race async sources.
            // Suppress input sync so a seeded unmatched value from onInitialMatchResolved is kept.
            this.suppressSelectedInputSync = true
            try {
                this.selected(null)
            } finally {
                this.suppressSelectedInputSync = false
            }
        }

        if (this.isOpen()) {
            this.setListOpen(true)
            if (this.isOpen()) {
                const current = this.currentItem()
                if (current == null || !items.some(o => this.isSameSelection(o, current))) {
                    const selected = this.selected()
                    this.currentItem(
                        selected != null && !this.isOptionDisabled(selected)
                            ? selected
                            : this.firstEnabledItem(items),
                    )
                }
            }
        }

        this.refreshWidthEstimate()
    }

    /** Re-read sync {@link IComboboxV2Options.data} into the prepared list. */
    private refreshFromSource(): void {
        this.applyPreparedItems(getAnyValue(this.params.data ?? []) ?? [])
    }

    /**
     * Re-run the promise factory (or re-read sync data). Assigned onto params as `reload`.
     * Overlapping calls ignore stale settles via {@link loadGeneration}.
     */
    private reload(): void {
        const factory = this.params.promise
        if (!factory) {
            this.refreshFromSource()
            this.isReady(true)
            this.isLoading(false)
            return
        }

        const generation = ++this.loadGeneration
        this.isLoading(true)
        this.isReady(false)

        factory()
            .then((items: T[]) => {
                if (generation !== this.loadGeneration) {
                    return
                }
                this.applyPreparedItems(items)
                this.isReady(true)
                this.isLoading(false)
            })
            .catch((reason: unknown) => {
                if (generation !== this.loadGeneration) {
                    return
                }
                // Keep previous `_data` / selection; field stays usable.
                this.isReady(true)
                this.isLoading(false)
                this.params.onError?.(reason)
            })
    }

    /**
     * Longest prepared-label length → `--combobox-v2-estimate-ch`.
     * Uses {@link _data} so live text-filter does not resize the dropdown.
     */
    private refreshWidthEstimate(): void {
        if (!this.isVirtualized()) {
            return
        }
        let max = 0
        for (const item of this._data() ?? []) {
            const len = this.optionLabel(item).length
            if (len > max) {
                max = len
            }
        }
        this.maxLabelChars(max)
    }

    private optionLabel(item: T): string {
        return this.params.option?.label?.(item) ?? String(item ?? '')
    }

    /** Input value for a committed selection ({@link IComboboxV2Options.inputAfterSelect} or label). */
    private inputTextForSelection(item: T): string {
        return this.params.inputAfterSelect?.(item) ?? this.optionLabel(item)
    }

    private optionHtml(item: T): string {
        return this.params.option?.html?.(item) ?? ''
    }

    private optionTooltip(item: T): string | undefined {
        return this.params.option?.tooltip?.(item) || undefined
    }

    private optionId(index: number): string {
        return `${this.listId}-option-${index}`
    }

    private isOptionDisabled(item: T): boolean {
        return this.params.option?.disabled?.(item) === true
    }

    /** First selectable row in `items`, or null when every row is disabled / empty. */
    private firstEnabledItem(items: T[]): T | null {
        return items.find(o => !this.isOptionDisabled(o)) ?? null
    }

    /** DOM attrs for an option row (id, title, aria, typed `data-type`, aria-disabled). */
    private optionDomAttrs(item: T, visibleIndex: number): Record<string, string | undefined> {
        const index = this.windowStartIndex() + visibleIndex
        const attrs: Record<string, string | undefined> = {
            id: this.optionId(index),
            title: this.optionTooltip(item),
            'aria-selected': this.isSelected(item).toString(),
            'aria-posinset': String(index + 1),
            'aria-setsize': String(this.optionCount()),
        }
        const dataType = this.params.option?.dataType?.(item)
        if (dataType != null && dataType !== '') {
            attrs['data-type'] = dataType
        }
        if (this.isOptionDisabled(item)) {
            attrs['aria-disabled'] = 'true'
        }
        return attrs
    }

    private isSelected(item: T): boolean {
        return this.isSameSelection(item, this.selected())
    }

    private isCurrent(item: T): boolean {
        return this.currentItem() === item
    }

    /** Case-insensitive: does the input currently show the selected item's label? */
    private inputMatchesSelected(): boolean {
        const selected = this.selected()
        if (selected == null) {
            return !(this.input() ?? '')
        }
        return (this.input() ?? '').toLowerCase() === this.inputTextForSelection(selected).toLowerCase()
    }

    /** Same choice by reference, or by equal {@link IComboboxV2Options.option.key} when provided. */
    private isSameSelection(a: T | null, b: T | null): boolean {
        if (a === b) {
            return true
        }
        if (a == null || b == null) {
            return false
        }
        const keyFn = this.params.option?.key
        if (!keyFn) {
            return false
        }
        return keyFn(a) == keyFn(b)
    }

    /**
     * Write selection + onSelect only when the choice changes.
     * When syncInput is true (default), also normalize/clear the input label.
     * When false, leave input as-is and suppress the selected→input subscription
     * (legacy unmatched blur: clear selected, keep typed text).
     * Returns whether selected/onSelect ran.
     */
    private commitSelection(item: T | null, syncInput = true): boolean {
        if (this.isSameSelection(item, this.selected())) {
            if (syncInput && item != null) {
                const label = this.inputTextForSelection(item)
                if ((this.input() ?? '') !== label) {
                    this.input(label)
                }
            }
            return false
        }
        if (syncInput) {
            this.selected(item)
            this.input(item != null ? this.inputTextForSelection(item) : '')
        } else {
            this.suppressSelectedInputSync = true
            try {
                this.selected(item)
            } finally {
                this.suppressSelectedInputSync = false
            }
        }
        this.params.onSelect?.(item)
        return true
    }

    /** Commit a list pick (click / Enter): close the list unless {@link selectionMode} is `pick`. */
    private selectItem(item: T): void {
        if (!this.isEnabled() || this.isOptionDisabled(item)) {
            return
        }
        if (this.params.selectionMode === 'pick') {
            this.params.onSelect?.(item)
            this.suppressSelectedInputSync = true
            try {
                this.selected(null)
            } finally {
                this.suppressSelectedInputSync = false
            }
            this.input('')
            this.currentItem(null)
            this.filterActive(false)
            this.isOpen(true)
            this.refreshInputChromeStatus()
            document.getElementById(this.inputElementId)?.focus()
            return
        }
        this.commitSelection(item)
        this.currentItem(item)
        this.filterActive(false)
        this.isOpen(false)
        this.refreshInputChromeStatus()
        // Select label so the next keystroke replaces (focus + legacy Enter parity).
        this.selectInputText(document.getElementById(this.inputElementId))
    }

    /** Resolve status from current selected + input (legacy validate default when omitted). */
    private resolveInputStatus(): TComboboxV2InputStatus {
        const selected = this.selected()
        const text = this.input() ?? ''
        const fn = this.params.inputStatus
        if (!fn) {
            return !!selected || !text ? 'ok' : 'warning'
        }
        return fn(selected, text)
    }

    /** Latch status chrome after blur/commit (not while typing). */
    private refreshInputChromeStatus(): void {
        this.inputChromeStatus(this.resolveInputStatus())
    }

    /** Closes the list and clears transient keyboard/filter state (does not settle input text). */
    private closeList(): void {
        this.isOpen(false)
        this.currentItem(null)
        this.filterActive(false)
    }

    /**
     * Commit or discard transient filter text when leaving the control.
     * Does not re-fire selection when the value is unchanged.
     * Same choice (case-insensitive) restores the canonical label; otherwise restore
     * last selection if {@link IComboboxV2Options.restoreLastValidOnBlur}, else clear
     * selected and keep typed text (legacy validate warning). Selection is never
     * inferred from typed text — only click / open-list Enter commit a choice.
     */
    private settleInputOnBlur(): void {
        try {
            // Same choice (case-insensitive) → no onSelect; still restore canonical label.
            if (this.inputMatchesSelected()) {
                const selected = this.selected()
                if (selected != null) {
                    this.commitSelection(selected)
                }
                return
            }
            if (this.params.restoreLastValidOnBlur && this.selected() != null) {
                this.input(this.inputTextForSelection(this.selected()!))
                return
            }
            // Clear selected, keep typed string → warning chrome.
            this.commitSelection(null, false)
        } finally {
            this.refreshInputChromeStatus()
        }
    }

    /** Restore the selected item's committed input text (keepLastValidObject parity). */
    private restoreSelectedLabel(): void {
        const selected = this.selected()
        if (selected == null) {
            return
        }
        this.input(this.inputTextForSelection(selected))
    }

    /**
     * Open the list when there are filtered rows, or when {@link emptyFilterMessageText}
     * applies (typed query, zero matches) — otherwise keep it closed (legacy select).
     */
    private setListOpen(open: boolean): void {
        if (!open) {
            this.isOpen(false)
            this.currentItem(null)
            return
        }
        const hasRows = this.items().length > 0
        const canShowEmptyFilter = !!this.emptyFilterMessageText
            && !hasRows
            && !!(this.input() ?? '').trim()
        this.isOpen(hasRows || canShowEmptyFilter)
        if (!this.isOpen()) {
            this.currentItem(null)
        }
    }

    private openListWithFilter(filtered: boolean): void {
        if (!this.isEnabled()) {
            return
        }
        this.listMounted(true)
        this.filterActive(filtered)
        this.setListOpen(true)
        if (!this.isOpen()) {
            return
        }
        if (filtered) {
            this.currentItem(this.firstEnabledItem(this.items()))
            const scrollElement = this.getScrollElement()
            if (scrollElement) {
                scrollElement.scrollTop = 0
                if (this.isVirtualized()) {
                    this.scrollTop(0)
                }
            }
        } else {
            const selected = this.selected()
            this.currentItem(
                selected != null && !this.isOptionDisabled(selected)
                    ? selected
                    : this.firstEnabledItem(this.items()),
            )
            this.scrollCurrentIntoView()
        }
    }

    /** Opens the list without text-filtering so a filled selection does not shrink the list. */
    private openList(): void {
        this.openListWithFilter(false)
    }

    /** Opens (or keeps open) with text filtering on — used when the user edits the input. */
    private openListFiltered(): void {
        this.openListWithFilter(true)
    }

    /**
     * Move the keyboard highlight by `delta`, skipping {@link option.disabled} rows.
     * At the ends: wrap only when `allowWrap` (fresh keypress); key-repeat clamps so hold-to-scroll does not spin.
     */
    private moveCurrent(delta: number, allowWrap: boolean): void {
        const items = this.items()
        if (!items.length) {
            return
        }
        const current = this.currentItem()
        let index = current == null ? -1 : items.indexOf(current)
        if (index < 0) {
            index = delta > 0 ? -1 : items.length
        }
        const step = delta > 0 ? 1 : -1
        let next = index
        for (let i = 0; i < items.length; i++) {
            next += step
            if (next < 0 || next >= items.length) {
                if (!allowWrap) {
                    return
                }
                next = (next % items.length + items.length) % items.length
            }
            const candidate = items[next]
            if (candidate != null && !this.isOptionDisabled(candidate)) {
                this.currentItem(candidate)
                this.scrollCurrentIntoView()
                return
            }
        }
    }

    private scrollCurrentIntoView(): void {
        const id = this.activeDescendantId()
        if (!id) {
            return
        }
        // Ignore hover while scrolling may move nodes under a stationary cursor.
        this.ignorePointerHighlight = true
        // First open mounts the list on this turn; wait a frame so option nodes exist.
        window.requestAnimationFrame(() => {
            this.measureVirtualMetrics()
            if (!this.scrollCurrentByIndex()) {
                document.getElementById(id)?.scrollIntoView({block: 'nearest'})
            }
            window.requestAnimationFrame(() => {
                this.ignorePointerHighlight = false
            })
        })
    }

    /** Cached lookup of the scroll container; re-resolved if the node was replaced. */
    private getScrollElement(): HTMLElement | null {
        if (!this.scrollElement?.isConnected) {
            this.scrollElement = document.getElementById(this.scrollElementId)
        }
        return this.scrollElement
    }

    /**
     * Aligns the virtual window with the real CSS: viewport height of the scroll container and the
     * height of one rendered row. Only meaningful while the list is open (it is `display: none` when
     * closed), so callers run it from the same frame that made the list visible.
     */
    private measureVirtualMetrics(): void {
        if (!this.isVirtualized()) {
            return
        }
        const scrollElement = this.getScrollElement()
        if (!scrollElement) {
            return
        }
        if (scrollElement.clientHeight > 0) {
            this.viewportHeight(scrollElement.clientHeight)
        }
        const rowHeight = scrollElement.querySelector('.combobox-v2__option')?.getBoundingClientRect().height ?? 0
        if (rowHeight > 0) {
            this.rowHeight(rowHeight)
        }
        if (!this.viewportObserver && typeof ResizeObserver !== 'undefined') {
            this.viewportObserver = new ResizeObserver(() => {
                const height = this.getScrollElement()?.clientHeight ?? 0
                if (height > 0) {
                    this.viewportHeight(height)
                }
            })
            this.viewportObserver.observe(scrollElement)
        }
    }

    /**
     * `scrollIntoView` cannot reach a highlighted row that is outside the rendered window, so scroll
     * the container by row index instead (`block: 'nearest'` semantics). Writing scrollTop re-renders
     * the window synchronously, which puts the row in the DOM for `aria-activedescendant`.
     * Returns false when not virtualizing so the caller falls back to `scrollIntoView`.
     */
    private scrollCurrentByIndex(): boolean {
        if (!this.isVirtualized()) {
            return false
        }
        const scrollElement = this.getScrollElement()
        const current = this.currentItem()
        if (!scrollElement || current == null) {
            return false
        }
        const index = this.items().indexOf(current)
        if (index < 0) {
            return false
        }
        const rowHeight = this.rowHeight()
        const rowTop = index * rowHeight
        const viewportTop = scrollElement.scrollTop
        const viewportSize = scrollElement.clientHeight
        if (rowTop < viewportTop) {
            scrollElement.scrollTop = rowTop
        } else if (rowTop + rowHeight > viewportTop + viewportSize) {
            scrollElement.scrollTop = rowTop + rowHeight - viewportSize
        }
        this.scrollTop(scrollElement.scrollTop)
        return true
    }

    /** Keeps the virtual window in sync with the scroll position. */
    private readonly onListScroll = (_data: unknown, event: Event): boolean => {
        if (this.isVirtualized()) {
            this.scrollTop((event.currentTarget as HTMLElement).scrollTop)
        }
        return true
    }

    /**
     * Delegated pointer move on the list: hover updates currentItem (option A — last hovered
     * stays current after mouseleave). Skips when ignorePointerHighlight is set.
     */
    private readonly onListMouseMove = (_data: unknown, event: MouseEvent): boolean => {
        if (this.ignorePointerHighlight) {
            return true
        }
        const target = event.target as Element | null
        const optionEl = target?.closest?.('.combobox-v2__option') as HTMLElement | null
        if (!optionEl) {
            return true
        }
        const item = ko.dataFor(optionEl) as T | undefined
        if (item === undefined || this.isOptionDisabled(item) || item === this.currentItem()) {
            return true
        }
        this.currentItem(item)
        return true
    }

    /**
     * Single delegated click on the root: resolve the option via closest + ko.dataFor
     * (see https://www.knockmeout.net/2012/11/revisit-event-delegation-in-knockout-js).
     * Avoids N click handlers on list items.
     */
    private readonly onRootClick = (_data: unknown, event: MouseEvent): boolean => {
        const target = event.target as Element | null
        const optionEl = target?.closest?.('.combobox-v2__option') as HTMLElement | null
        if (!optionEl) {
            return true
        }
        const item = ko.dataFor(optionEl) as T | undefined
        if (item === undefined || this.isOptionDisabled(item)) {
            return true
        }
        this.selectItem(item)
        return false
    }

    /** Keyboard on the focused input (↑/↓ open or move, Enter commits, Escape closes). */
    private readonly onInputKeydown = (_data: unknown, event: KeyboardEvent): boolean => {
        if (!this.isEnabled()) {
            return true
        }
        // Knockout may pass a jQuery event — read repeat from the native KeyboardEvent.
        const isRepeat = (event as unknown as JQuery.KeyDownEvent).originalEvent?.repeat
            ?? event.repeat
            ?? false
        switch (event.key) {
            case 'ArrowDown': {
                if (!this.isOpen()) {
                    this.openList()
                } else {
                    this.moveCurrent(1, !isRepeat)
                }
                return false
            }
            case 'ArrowUp': {
                if (!this.isOpen()) {
                    this.openList()
                } else {
                    this.moveCurrent(-1, !isRepeat)
                }
                return false
            }
            case 'Enter': {
                if (!this.isOpen()) {
                    if (this.params.restoreLastValidOnBlur) {
                        this.restoreSelectedLabel()
                        this.refreshInputChromeStatus()
                    } else if (this.input()) {
                        // Do not infer a choice from typed text; clear selected, keep string.
                        this.commitSelection(null, false)
                        this.refreshInputChromeStatus()
                    } else {
                        this.commitSelection(null)
                        this.refreshInputChromeStatus()
                    }
                    return true
                }
                const current = this.currentItem()
                if (current != null && !this.isOptionDisabled(current)) {
                    this.selectItem(current)
                }
                return false
            }
            case 'Escape': {
                if (!this.isOpen()) {
                    return true
                }
                this.closeList()
                return false
            }
            default:
                return true
        }
    }

    /** Select all input text so the next keystroke replaces the value. */
    private selectInputText(el: EventTarget | null): void {
        (el as HTMLInputElement | null)?.select()
    }

    private readonly onWindowBlur = (): void => {
        this.selectAllPending = true
    }

    /** Decide whether this pointer gesture should select-all (fresh focus or after window blur). */
    private readonly onInputMouseDown = (_data: unknown, event: MouseEvent): boolean => {
        if (!this.isEnabled()) {
            return true
        }
        this.selectAllThisGesture =
            this.selectAllPending || document.activeElement !== event.target
        return true
    }

    /** Tab / programmatic focus: select all; clear status chrome (legacy focus clear). */
    private readonly onInputFocus = (_data: unknown, event: FocusEvent): boolean => {
        if (!this.isEnabled()) {
            return true
        }
        this.inputChromeStatus('ok')
        this.selectInputText(event.target)
        return true
    }

    /** On select-all gestures, keep selection from being replaced by the caret. */
    private readonly onInputMouseUp = (_data: unknown, event: MouseEvent): boolean => {
        if (!this.isEnabled() || !this.selectAllThisGesture) {
            return true
        }
        this.selectInputText(event.target)
        event.preventDefault()
        this.selectAllPending = false
        return true
    }

    /** Pointer activation opens the list; focus alone (Tab) deliberately does not. */
    private readonly onInputClick = (_data: unknown, event: MouseEvent): boolean => {
        if (!this.isEnabled()) {
            return true
        }
        this.openList()
        if (this.selectAllThisGesture) {
            // Re-select: openList aria attr updates can clear the mouseup selection.
            this.selectInputText(event.target)
        }
        this.selectAllThisGesture = false
        return true
    }

    /** Editing the text enables filtering and opens the list. */
    private readonly onInputTyping = (): boolean => {
        if (!this.isEnabled()) {
            return true
        }
        this.openListFiltered()
        return true
    }

    /**
     * Returning false makes Knockout preventDefault, so pressing inside the list (option row or
     * scrollbar) never moves focus off the input — scrolling the list cannot close it.
     */
    private readonly onListMouseDown = (): boolean => {
        return false
    }

    /** Close only when focus leaves the whole component, not when moving inside it. */
    private readonly onFocusOut = (_data: unknown, event: FocusEvent): boolean => {
        const wrapper = event.currentTarget as HTMLElement | null
        if (wrapper?.contains(event.relatedTarget as Node | null)) {
            return true
        }
        this.closeList()
        this.settleInputOnBlur()
        return true
    }

    dispose(): void {
        // Bump first so an abort-triggered reject is ignored as a stale generation.
        this.loadGeneration++
        this.params.onDispose?.()
        window.removeEventListener('blur', this.onWindowBlur)
        this.viewportObserver?.disconnect()
        this.viewportObserver = null
        this.scrollElement = null
        this.dataSubscription?.dispose()
        this.reloadWhenSubscription?.dispose()
        this.enableSubscription.dispose()
        this.selectedSubscription.dispose()
        this.isEnabled.dispose()
        this.inputPhase.dispose()
        this.items.dispose()
        this.showEmptyFilterMessage.dispose()
        this.isListVisible.dispose()
        this.optionCount.dispose()
        this.visibleWindow.dispose()
        this.windowStartIndex.dispose()
        this.windowEndIndex.dispose()
        this.visibleItems.dispose()
        this.optionsSpacerStyle.dispose()
        this.activeDescendantId.dispose()
        this.inputDomAttrs.dispose()
        this.listDomStyle.dispose()
        this.isVirtualized.dispose()
        this.isTextEllipsis.dispose()
    }
}
